import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '@/components/layout/PublicLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle2, Award, Users, Globe, Target, Eye, Heart,
  Building2, GraduationCap, Briefcase, ArrowRight
} from 'lucide-react';

export default function AboutPage() {
  const navigate = useNavigate();

  const expertise = [
    { icon: CheckCircle2, text: 'Individual Tax Filing' },
    { icon: Building2, text: 'Business Tax Returns' },
    { icon: Briefcase, text: 'LLC Formation' },
    { icon: Award, text: 'Maximum Refund Guarantee' },
    { icon: Users, text: 'Expert Support' },
    { icon: Globe, text: 'All 50 States' }
  ];

  const values = [
    { 
      icon: Target,
      title: 'Simple & Easy', 
      description: 'Tax filing that just works. No jargon, no confusion.' 
    },
    { 
      icon: CheckCircle2,
      title: '100% Accurate', 
      description: 'Guaranteed accuracy or we pay the penalties' 
    },
    { 
      icon: Eye,
      title: 'Maximum Refund', 
      description: 'Get every dollar you deserve back' 
    },
    { 
      icon: Heart,
      title: 'Real Support', 
      description: 'Talk to real tax pros whenever you need help' 
    }
  ];

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 py-24">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Why Choose Kosha Tax
            </h1>
            <p className="text-lg text-slate-300">
              We make taxes and LLC formation simple, affordable, and accurate. 
              Get your maximum refund with confidence.
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        {/* Who We Are */}
        <section className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Simple. Accurate. Guaranteed.</h2>
              <div className="space-y-4 text-lg text-muted-foreground">
                <p>
                  Kosha Tax is designed to make tax filing and business formation as simple as possible. 
                  Whether you're filing your personal taxes or starting an LLC, we guide you through every step.
                </p>
                <p>
                  Our platform combines easy-to-use tools with expert support, so you can file with confidence 
                  and get your maximum refund.
                </p>
                <p>
                  We guarantee 100% accuracy. If we make a mistake, we pay any IRS penalties.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?w=600" 
                alt="Professional team collaboration" 
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white dark:bg-gray-900 rounded-xl p-6 shadow-lg border">
                <div className="text-4xl font-bold text-primary">15+</div>
                <div className="text-muted-foreground">Years of Excellence</div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="mb-20">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-primary/20 bg-primary/5">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <Target className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
                <p className="text-muted-foreground text-lg">
                  To simplify financial complexity and empower our clients with accurate, 
                  compliant, and strategic financial services that help them achieve their goals.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-500/20 bg-blue-500/5">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
                  <Eye className="h-7 w-7 text-blue-500" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
                <p className="text-muted-foreground text-lg">
                  To be the most trusted global partner for cross-border financial services, 
                  known for excellence, integrity, and client-centric solutions.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Our Expertise */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <Badge className="mb-4">Our Expertise</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Areas of Specialization</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our team brings deep expertise across a wide range of financial disciplines
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {expertise.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <span className="font-medium">{item.text}</span>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Our Values */}
        <section className="mb-20 bg-gray-50 dark:bg-gray-800/50 rounded-3xl p-8 md:p-12">
          <div className="text-center mb-12">
            <Badge className="mb-4">Our Values</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Drives Us</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="text-center border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-8 md:p-12 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Work With Us?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied clients who trust Kosha Tax for their financial needs.
          </p>
          <Button size="lg" variant="secondary" onClick={() => navigate('/login')}>
            Get Started Today <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </section>
      </div>
    </PublicLayout>
  );
}